import moment, { Moment } from 'moment';
import { useState, useEffect, useCallback, useRef } from 'react';
import { TCondicaoParcelasAPI, TListaParcelasAPI, TSimularParcelamentoRequest, TSimularParcelamentoResponse } from './SelecaoCondicaoParcelamentoAPI';
import { useMutation } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import axiosClient from '../../lib/axios';

export default function useParcelasAPI(
    defaultValues: TCondicaoParcelasAPI,
    valorTotal: number,
    isCartao: boolean = false,
    isCheque: boolean = false
) {
    const { qtdParcelas, condicao_pagamento_id, tipo_publico, codigo_publico, forma_recebimento_id, diaVencimentoPadrao } = defaultValues;
    const [parcelas, setParcelas] = useState<Array<TListaParcelasAPI>>([]);
    const [valorTotalComJuros, setValorTotalComJuros] = useState<number>(0);
    const [percentualJuros, setPercentualJuros] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    // Refs para evitar loops infinitos
    const lastCalculatedRef = useRef<string>('');
    const isInitializedRef = useRef<boolean>(false);

    //Querys e mutations
    const { mutate: calcularParcelas, isLoading: loading } = useMutation({
        mutationKey: 'calcular-parcelas',
        mutationFn: (inputs: TSimularParcelamentoRequest) => axiosClient.post("/comercial/pedidos/simular-parcelamento", { ...inputs }),
        onError: (error) => {
            console.error(error)
            setParcelas([]);
            setValorTotalComJuros(0);
            setPercentualJuros(0);
            enqueueSnackbar('Erro ao atualizar parcelas!', { variant: 'error' });
        },
        onSuccess: (data) => {
            const dataSimulacao: TSimularParcelamentoResponse = data.data;
            setParcelas(dataSimulacao.parcelas);
            setValorTotalComJuros(dataSimulacao.valor);
            setPercentualJuros(dataSimulacao.percentual_juros);
        }
    })

    // Functions
    const simularParcelamento = useCallback(async (datasVencimento?: string[]) => {
        if (!condicao_pagamento_id || !forma_recebimento_id || qtdParcelas <= 0 || valorTotal <= 0) {
            return;
        }

        // Criar chave única para evitar chamadas desnecessárias
        const chaveCalculo = `${condicao_pagamento_id}-${qtdParcelas}-${valorTotal}-${datasVencimento?.join(',')}`;

        // Só usar cache se não tivermos datas específicas (mudanças manuais)
        // Se temos datas específicas, sempre fazer a chamada para garantir que mudanças nas datas sejam processadas
        if (!datasVencimento && lastCalculatedRef.current === chaveCalculo) {
            return;
        }

        setError(null);
        lastCalculatedRef.current = chaveCalculo;

        const requestData: TSimularParcelamentoRequest = {
            condicao_pagamento_id,
            valor: valorTotal,
            parcelas: qtdParcelas,
            forma_recebimento_id,
            tipo_publico,
            codigo_publico: parseInt(codigo_publico.toString()),
            ...(datasVencimento && { datas_vencimento: datasVencimento })
        };

        calcularParcelas(requestData);

    }, [condicao_pagamento_id, forma_recebimento_id, qtdParcelas, valorTotal, calcularParcelas]);

    const calcularDatasDefault = useCallback(() => {
        let dataVencimento: moment.Moment;

        if ((isCartao || isCheque) && qtdParcelas === 1) {
            // Para cartão ou cheque com 1 parcela, usar a data atual
            dataVencimento = moment();
        } else if ((isCartao || isCheque) && qtdParcelas > 1) {
            // Para cartão ou cheque com mais de 1 parcela, começar do próximo mês
            dataVencimento = moment().add(1, "months");
        } else {
            // Para outras formas de pagamento, começar do próximo mês
            dataVencimento = moment().add(1, "months").date(diaVencimentoPadrao);
        }

        const datas: string[] = [];

        for (let i = 0; i < qtdParcelas; i++) {
            if (i === 0) {
                // Primeira parcela: usar a data calculada acima
                datas.push(dataVencimento.format("YYYY-MM-DD"));
            } else {
                // Próximas parcelas: adicionar 1 mês
                dataVencimento = dataVencimento.add(1, "months");
                datas.push(dataVencimento.format("YYYY-MM-DD"));
            }
        }

        return datas;
    }, [qtdParcelas, isCartao, isCheque, diaVencimentoPadrao]);

    const limparEstados = useCallback(() => {
        setParcelas([]);
        setValorTotalComJuros(0);
        setPercentualJuros(0);
        setError(null);
        lastCalculatedRef.current = '';
    }, []);

    const handleDataChange = useCallback(async (index: number, novaData: Moment) => {
        const maxParcelas = defaultValues.maximo_parcelas;
        const quantidadeParcelas = parcelas.length;
        const datasAtuais = parcelas.map(p => moment(p.vencimento, "DD/MM/YYYY").format("YYYY-MM-DD"));
        datasAtuais[index] = novaData.format("YYYY-MM-DD");

        const dataLimiteUltimaParcela = moment().add(maxParcelas, "months").endOf("month");

        const datasLimites: Moment[] = [];
        for (let i = 0; i < quantidadeParcelas; i++) {
            const diasRestantes = quantidadeParcelas - 1 - i;
            datasLimites[i] = moment(dataLimiteUltimaParcela).subtract(diasRestantes, "days");
        }

        // Atualizar as próximas parcelas com base na nova data
        for (let i = index + 1; i < datasAtuais.length; i++) {
            const vencimentoAnterior = moment(datasAtuais[i - 1]);
            const proximoVencimento = vencimentoAnterior.clone().add(1, "months");

            datasAtuais[i] = proximoVencimento.isAfter(datasLimites[i])
                ? datasLimites[i].format("YYYY-MM-DD")
                : proximoVencimento.format("YYYY-MM-DD");
        }

        await simularParcelamento(datasAtuais);
    }, [parcelas, simularParcelamento]);

    //

    useEffect(() => {

        if (!condicao_pagamento_id || qtdParcelas === 0 || !forma_recebimento_id) {
            limparEstados();
            return;
        }

        if (defaultValues.parcelas.length > 0 && qtdParcelas === defaultValues.parcelas.length && !isInitializedRef.current) {
            const somaParcelas = defaultValues.parcelas.reduce((acc, p) => acc + p.valor, 0);
            setParcelas(defaultValues.parcelas);
            setValorTotalComJuros(somaParcelas);
            if (valorTotal > 0) {
                const prct = ((somaParcelas - valorTotal) / valorTotal) * 100;
                setPercentualJuros(Number(prct.toFixed(2)));
            } else {
                setPercentualJuros(0);
            }
            isInitializedRef.current = true;
            return;
        }

        // Calcular novas parcelas apenas se necessário
        if (qtdParcelas > 0 && valorTotal > 0) {
            const datasDefault = calcularDatasDefault();
            simularParcelamento(datasDefault);
        }

        isInitializedRef.current = true;
    }, [qtdParcelas, valorTotal, condicao_pagamento_id]);

    useEffect(() => {
        if (defaultValues.parcelas.length > 0 && defaultValues.parcelas.length === qtdParcelas) {
            const somaParcelas = defaultValues.parcelas.reduce((acc, p) => acc + p.valor, 0);
            setParcelas(defaultValues.parcelas);
            setValorTotalComJuros(somaParcelas);
            if (valorTotal > 0) {
                const prct = ((somaParcelas - valorTotal) / valorTotal) * 100;
                setPercentualJuros(Number(prct.toFixed(2)));
            } else {
                setPercentualJuros(0);
            }
        }
    }, [defaultValues.parcelas, qtdParcelas, valorTotal]);

    useEffect(() => {
        return () => {
            lastCalculatedRef.current = '';
            isInitializedRef.current = false;
        };
    }, []);

    return {
        listaParcelas: parcelas,
        valorTotalComJuros,
        percentualJuros,
        handleDataChange,
        loading,
        error
    };
}